import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { terminal } from './app/terminal.js';
import { useApp } from '@weke/ui';
import type { AppState } from '@weke/ui';
import { BannerStack } from '@weke/ui';
import { ChartPanel } from './components/ChartPanel.js';
import { MenuBar, Toolbar } from './components/Chrome.js';
import type { ChromePanel } from './components/Chrome.js';
import { Login } from './components/Login.js';
import { MarketWatch } from './components/MarketWatch.js';
import { Navigator } from './components/Navigator.js';
import { OrderTicketPanel } from './components/OrderTicket.js';
import { Splitter } from '@weke/ui';
import { Toolbox } from './components/Toolbox.js';
import { statementCsv } from '@weke/ui';
import { formatMoney, formatPercentBp } from '@weke/ui';
import { serverClock } from '@weke/ui';

/**
 * The desk shell.
 *
 * Fixed arrangement, not a dock: navigator and market watch on the left, charts in
 * the middle, the order ticket on the right, the account's book across the bottom.
 * That is the five-region layout a trader already knows, so nothing has to be
 * learned before the first order; the dividers still drag and the sizes persist.
 *
 * The top is two thin rows — a menu strip and a toolbar — because a large hero
 * header is vertical space the chart could be using.
 *
 * A chart tab is a *view*, not a document: its symbol and interval live here so
 * the MDI strip under the chart can add and close them, and so switching tabs
 * does not remount the client.
 */

interface ChartTab {
  readonly id: number;
  readonly symbolName: string;
  readonly minutes: number;
}

const SIZES_KEY = 'weke.terminal.sizes.v1';
interface Sizes {
  left: number;
  right: number;
  bottom: number;
}
const DEFAULT_SIZES: Sizes = { left: 272, right: 268, bottom: 232 };
const LIMITS = {
  left: { min: 200, max: 420 },
  right: { min: 236, max: 420 },
  bottom: { min: 120, max: 460 },
} as const;

function loadSizes(): Sizes {
  try {
    const raw = window.localStorage.getItem(SIZES_KEY);
    if (raw === null) return DEFAULT_SIZES;
    const parsed = JSON.parse(raw) as Partial<Sizes>;
    return {
      left: clamp(parsed.left, LIMITS.left),
      right: clamp(parsed.right, LIMITS.right),
      bottom: clamp(parsed.bottom, LIMITS.bottom),
    };
  } catch {
    return DEFAULT_SIZES;
  }
}

function clamp(value: number | undefined, range: { min: number; max: number }): number {
  if (value === undefined || !Number.isFinite(value)) return range.min;
  return Math.min(range.max, Math.max(range.min, Math.round(value)));
}

export function App(): ReactElement {
  const app = useApp();
  useTicker();
  if (app.session === null) {
    return (
      <>
        <Login />
        <BannerStack banners={app.banners} />
      </>
    );
  }
  return <Shell app={app} />;
}

/** Re-render once a second so the server clock keeps up. */
function useTicker(): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 1_000);
    return () => clearInterval(timer);
  }, []);
}

function Shell({ app }: { readonly app: AppState }): ReactElement {
  const [sizes, setSizes] = useState<Sizes>(loadSizes);
  const [hidden, setHidden] = useState<readonly ChromePanel[]>([]);
  const [charts, setCharts] = useState<ChartTab[]>(() => [
    { id: 1, symbolName: app.selectedSymbol ?? 'EURUSD', minutes: 1 },
  ]);
  const [activeChart, setActiveChart] = useState(1);

  /**
   * The same CSV the History tab builds, reachable from `File → Export`.
   * P8 owns the official, server-generated statement; this one says so in its
   * header line and is a convenience, not a record.
   */
  const exportStatement = (): void => {
    const csv = statementCsv(app.deals, {
      currency: app.account?.currency ?? "USD",
      digits: app.account?.digits ?? 2,
      accountId: app.session?.accountId ?? "",
      generatedAt: new Date().toISOString(),
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `weke-statement-${(app.session?.accountId ?? "account").slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(SIZES_KEY, JSON.stringify(sizes));
    } catch {
      // A full quota must not break the terminal; the sizes just do not persist.
    }
  }, [sizes]);

  /* The watchlist selection drives the active chart, like every terminal. */
  useEffect(() => {
    if (app.selectedSymbol === null) return;
    setCharts((current) =>
      current.map((chart) => (chart.id === activeChart ? { ...chart, symbolName: app.selectedSymbol as string } : chart)),
    );
  }, [app.selectedSymbol, activeChart]);

  const toggle = (id: ChromePanel): void => {
    setHidden((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));
  };
  const show = (id: ChromePanel): boolean => !hidden.includes(id);
  const active = charts.find((chart) => chart.id === activeChart) ?? charts[0];
  const addChart = (): void => {
    const symbolName = app.selectedSymbol ?? active?.symbolName ?? 'EURUSD';
    const id = charts.reduce((max, chart) => Math.max(max, chart.id), 0) + 1;
    setCharts((current) => [...current, { id, symbolName, minutes: active?.minutes ?? 1 }]);
    setActiveChart(id);
  };
  const closeChart = (id: number): void => {
    setCharts((current) => {
      const remaining = current.filter((chart) => chart.id !== id);
      if (remaining.length === 0) return current;
      if (id === activeChart) setActiveChart(remaining[remaining.length - 1]?.id ?? remaining[0]?.id ?? 1);
      return remaining;
    });
  };

  return (
    <div className="flex h-full flex-col bg-bg">
      <MenuBar
        onSignOut={() => void terminal.logout()}
        onExport={exportStatement}
        onInterval={(minutes) => {
          if (active === undefined) return;
          setCharts((current) =>
            current.map((chart) => (chart.id === active.id ? { ...chart, minutes } : chart)),
          );
        }}
        onTogglePanel={toggle}
        onResetLayout={() => {
          setSizes(DEFAULT_SIZES);
          setHidden([]);
        }}
        onNewChart={addChart}
        onReload={() => {
          void terminal.pollSnapshot();
          void terminal.refreshDeals();
        }}
      />
      <Toolbar
        onSignOut={() => void terminal.logout()}
        onExport={exportStatement}
        onInterval={(minutes) => {
          if (active === undefined) return;
          setCharts((current) =>
            current.map((chart) => (chart.id === active.id ? { ...chart, minutes } : chart)),
          );
        }}
        onTogglePanel={toggle}
        onReload={() => {
          void terminal.pollSnapshot();
          void terminal.refreshDeals();
        }}
      />

      <div className="flex min-h-0 flex-1">
        {show('watch') ? (
          <aside style={{ width: sizes.left }} className="flex min-h-0 flex-none flex-col border-r border-line">
            <MarketWatch />
            {show('navigator') ? <Navigator /> : null}
          </aside>
        ) : null}
        {show('watch') || show('navigator') ? (
          <Splitter
            axis="x"
            label="Resize the left column"
            value={sizes.left}
            min={LIMITS.left.min}
            max={LIMITS.left.max}
            onChange={(left) => setSizes((current) => ({ ...current, left }))}
          />
        ) : null}

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col">
            {active === undefined ? null : (
              <ChartPanel
                key={active.id}
                symbolName={active.symbolName}
                minutes={active.minutes}
                onInterval={(minutes) => {
                  setCharts((current) =>
                    current.map((chart) => (chart.id === active.id ? { ...chart, minutes } : chart)),
                  );
                }}
                footer={
                  <div className="tabs" role="tablist" aria-label="Chart tabs">
                    {charts.map((chart) => (
                      <button
                        key={chart.id}
                        type="button"
                        role="tab"
                        className="tab"
                        aria-selected={chart.id === activeChart}
                        data-testid={`chart-tab-${chart.id}`}
                        onClick={() => {
                          setActiveChart(chart.id);
                          terminal.selectSymbol(chart.symbolName);
                        }}
                      >
                        {chart.symbolName},{intervalLabel(chart.minutes)}
                        {charts.length > 1 ? (
                          <span
                            role="presentation"
                            className="faint hover:text-text"
                            data-testid={`chart-tab-close-${chart.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              closeChart(chart.id);
                            }}
                          >
                            ×
                          </span>
                        ) : null}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="tab"
                      title="New chart tab"
                      aria-label="New chart tab"
                      data-testid="chart-tab-add"
                      onClick={addChart}
                    >
                      +
                    </button>
                  </div>
                }
              />
            )}
          </div>
          {show('toolbox') ? (
            <>
              <Splitter
                axis="y"
                label="Resize the toolbox"
                value={sizes.bottom}
                min={LIMITS.bottom.min}
                max={LIMITS.bottom.max}
                onChange={(bottom) => setSizes((current) => ({ ...current, bottom }))}
              />
              <div style={{ height: sizes.bottom }} className="flex min-h-0 flex-none flex-col">
                <Toolbox />
              </div>
            </>
          ) : null}
        </main>

        <Splitter
          axis="x"
          label="Resize the order ticket"
          value={sizes.right}
          min={LIMITS.right.min}
          max={LIMITS.right.max}
          invert
          onChange={(right) => setSizes((current) => ({ ...current, right }))}
        />
        <aside style={{ width: sizes.right }} className="min-h-0 flex-none border-l border-line">
          <OrderTicketPanel />
        </aside>
      </div>

      <StatusBar app={app} />
      <BannerStack banners={app.banners} />
    </div>
  );
}

function intervalLabel(minutes: number): string {
  if (minutes === 1440) return 'D1';
  if (minutes >= 60) return `H${minutes / 60}`;
  return `M${minutes}`;
}

function StatusBar({ app }: { readonly app: AppState }): ReactElement {
  const clock = serverClock(app.serverOffsetMs, Date.now());
  return (
    <footer className="flex h-[19px] flex-none items-center gap-3 overflow-hidden whitespace-nowrap border-t border-line bg-surface px-2 text-2xs text-muted">
      <span data-testid="status-cursor" title="The last account_seq this terminal applied">
        seq <span className="num text-text">{app.book.cursor.toString()}</span>
        {app.book.replay === null ? null : (
          <span className="faint">
            {' '}
            · replayed {app.book.replay.events} from {app.book.replay.fromSeq} through{' '}
            {app.book.replay.throughSeq}
          </span>
        )}
        {app.book.gapFrom === null ? null : <span className="loss"> · gap</span>}
      </span>
      <span className="faint">·</span>
      <span title="Round-trip time of the last PING">
        ping{' '}
        <span className="num text-text">
          {app.latencyMs === null ? '—' : `${app.latencyMs} ms`}
        </span>
      </span>
      <span className="faint">·</span>
      <span title="Server clock, corrected by the offset in HELLO">
        <span className="num text-text" data-testid="server-clock">
          {clock}
        </span>{' '}
        server
      </span>
      <span className="faint">·</span>
      <span
        title="The account snapshot the position Now and P&L columns were valued at (one poll behind the live quotes)"
        data-testid="marks-at"
      >
        marks{' '}
        <span className="num text-text">
          {app.snapshotAt === null ? '—' : app.snapshotAt.slice(11, 19)}
        </span>
      </span>
      <span className="faint">·</span>
      <span className="faint" title={`last message ${app.lastMessageAt ?? '—'}`}>
        {app.book.applied} applied · {app.book.duplicatesIgnored} dup
      </span>
      <span className="faint">·</span>
      <span className="num text-text" data-testid="header-balance">
        {app.account === null ? '—' : formatMoney(app.account.balance_minor, app.account.digits)}
      </span>
      <span className="faint">{app.account?.currency ?? ''}</span>
      <span className="faint">·</span>
      <span>
        equity{' '}
        <span className="num text-text">
          {app.account === null ? '—' : formatMoney(app.account.equity_minor, app.account.digits)}
        </span>
      </span>
      <span>
        free{' '}
        <span className="num text-text">
          {app.account === null ? '—' : formatMoney(app.account.free_margin_minor, app.account.digits)}
        </span>
      </span>
      <span>
        level{' '}
        <span className={`num ${app.account?.margin_call === true ? 'sell' : 'text-text'}`}>
          {app.account === null ? '—' : formatPercentBp(app.account.margin_level_bp)}
        </span>
      </span>
      <span className="ml-auto truncate text-faint" data-testid="connection-detail">
        {app.statusDetail ?? (app.accepting ? 'engine accepting' : 'engine not accepting')}
      </span>
    </footer>
  );
}
